import { useEffect, useState } from 'react'
import { Theme } from '@emotion/react'
import { CircularProgress, SxProps, TextField } from '@mui/material'
import Backdrop from '@mui/material/Backdrop'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Fade from '@mui/material/Fade'
import Modal from '@mui/material/Modal'
import Typography from '@mui/material/Typography'
import { THEME } from '../../global'
import { useInputText } from '../../hooks'
import user from '../../services/user'
import { PageAlert } from '../page-alert'

const style: SxProps<Theme> = {
  position: 'absolute',
  top: '40%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '85%',
  borderRadius: 2,
  bgcolor: THEME.colors.tertiary,
  boxShadow: 24,
  p: 4,
}

interface IAppModalProps {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  type?: 'password' | 'name' | 'email'
  data?: string
}

export function AppModal({ open, setOpen, data, type }: IAppModalProps) {
  const handleClose = () => setOpen(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)
  const capitalizedText = `${type?.charAt(0).toUpperCase()}${type?.slice(1)}`

  const initialValues = {
    email: type === 'email' ? data : undefined,
    name: type === 'name' ? data : undefined,
    oldPassword: type === 'password' ? data : undefined,
    newPassword: '',
  }

  const input = useInputText(initialValues)

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setIsLoading(true)

    await user
      .update({
        email: input.email,
        name: input.name,
        newPassword: input.newPassword,
        oldPassword: input.oldPassword,
      })
      .catch(() => {
        setIsError(true)
      })

    setIsLoading(false)
    handleClose()
  }

  useEffect(() => {
    !open && input.clear()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  return (
    <div>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={open}
        onClose={handleClose}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
          },
        }}
      >
        <Fade in={open}>
          <Box sx={style}>
            <div className="flex flex-col items-center">
              <Typography
                id="transition-modal-title"
                variant="h6"
                component="h2"
              >
                Nome completo
              </Typography>

              <Box
                component="form"
                noValidate
                autoComplete="off"
                onSubmit={handleSubmit}
              >
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name={type}
                  autoFocus
                  focused
                  onChange={input.onChange}
                  label={capitalizedText}
                  type={type !== 'password' ? 'text' : type}
                  id={type}
                />

                {type === 'password' && (
                  <TextField
                    margin="normal"
                    name="oldPassword"
                    required
                    variant="outlined"
                    fullWidth
                    focused
                    autoFocus
                    onChange={input.onChange}
                    label="Palavra passe antiga"
                    type="password"
                    id="oldPassword"
                  />
                )}

                {isError && (
                  <PageAlert
                    sx={{ display: 'flex', p: 0, mt: 2, border: 'none' }}
                    severity="error"
                    alertName="Não foi possível atualizar, tente novamente!"
                  />
                )}

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={isLoading}
                  sx={{ mt: 2, mb: 2, minHeight: 46 }}
                >
                  {!isLoading ? 'Guardar' : <CircularProgress size={24} />}
                </Button>
              </Box>
            </div>
          </Box>
        </Fade>
      </Modal>
    </div>
  )
}
